<template>
  <div class="h-full bg-gray-50 dark:bg-gray-950 flex flex-col">
    <!-- Compact Header -->
    <header class="flex-shrink-0 bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-800">
      <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center justify-between h-16">
          <div class="flex items-center space-x-3">
            <div :class="[themeColors.bg, 'w-9 h-9 rounded-lg flex items-center justify-center shadow-sm']">
              <svg class="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
              </svg>
            </div>
            <div>
              <h1 class="text-base font-semibold text-gray-900 dark:text-white">{{ agentName || 'AI Assistant' }}</h1>
              <p class="text-xs text-gray-500 dark:text-gray-400">
                <span v-if="companyName">{{ companyName }}</span>
                <span v-else-if="isValidating">Validating...</span>
                <span v-else>Ready to help</span>
              </p>
            </div>
          </div>
          <div class="flex items-center space-x-3">
            <div v-if="isValid" class="hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-emerald-50 dark:bg-emerald-900/20 rounded-full border border-emerald-200 dark:border-emerald-800/50">
              <div class="w-1.5 h-1.5 bg-emerald-500 rounded-full"></div>
              <span class="text-xs font-medium text-emerald-700 dark:text-emerald-300">Online</span>
            </div>
            <div v-else-if="!isValidating" class="hidden sm:flex items-center space-x-2 px-3 py-1.5 bg-red-50 dark:bg-red-900/20 rounded-full border border-red-200 dark:border-red-800/50">
              <div class="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span class="text-xs font-medium text-red-700 dark:text-red-300">Not Allowed</span>
            </div>
          </div>
        </div>
      </div>
    </header>

    <!-- Chat Area -->
    <main class="flex-1 flex flex-col overflow-hidden min-h-0">
      <!-- Loading State -->
      <div v-if="isValidating" class="flex-1 flex items-center justify-center bg-white dark:bg-gray-900">
        <div class="text-center">
          <div :class="[themeColors.iconBg, 'w-16 h-16 mx-auto mb-4 rounded-2xl flex items-center justify-center border']" :style="{ borderColor: themeColors.text + '40' }">
            <svg :class="[themeColors.text, 'w-8 h-8 animate-spin']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
            </svg>
          </div>
          <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-2">Validating access...</h2>
          <p class="text-sm text-gray-500 dark:text-gray-400">Checking domain and token</p>
        </div>
      </div>

      <!-- Not Allowed State -->
      <div v-else-if="!isValid" class="flex-1 flex items-center justify-center bg-white dark:bg-gray-900">
        <div class="text-center max-w-md px-4">
          <div class="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 dark:bg-red-900/20 flex items-center justify-center border border-red-100 dark:border-red-800/50">
            <svg class="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          <h2 class="text-2xl font-semibold text-gray-900 dark:text-white mb-2">Access Not Allowed</h2>
          <p class="text-sm text-gray-600 dark:text-gray-400 mb-4">{{ error || 'This domain is not approved for this AI agent.' }}</p>
          <div class="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 text-left">
            <p class="text-xs text-gray-600 dark:text-gray-400 mb-2"><strong>Current Domain:</strong> {{ domain }}</p>
            <p class="text-xs text-gray-600 dark:text-gray-400"><strong>Token:</strong> {{ token ? `${token.substring(0, 8)}...` : 'Not provided' }}</p>
          </div>
          <p class="text-xs text-gray-500 dark:text-gray-500 mt-4">
            Please contact your administrator to add this domain to the approved list.
          </p>
        </div>
      </div>

      <!-- Chat Interface (only shown when valid) -->
      <div v-else class="flex-1 overflow-y-auto bg-white dark:bg-gray-900 min-h-0" ref="messagesContainer">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <!-- Welcome State -->
          <div v-if="messages.length === 0 && responses.length === 0" class="flex flex-col items-center justify-center py-8 min-h-0">
            <div :class="[themeColors.iconBg, 'w-12 h-12 mb-4 rounded-xl flex items-center justify-center border']" :style="{ borderColor: themeColors.text + '40' }">
              <svg :class="[themeColors.text, 'w-6 h-6']" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"></path>
              </svg>
            </div>
            <h2 class="text-xl font-semibold text-gray-900 dark:text-white mb-1">How can I help you today?</h2>
            <p class="text-sm text-gray-500 dark:text-gray-400 mb-4">Ask me anything and I'll do my best to assist you.</p>
          </div>

          <!-- Messages -->
          <div v-else class="space-y-4 pb-4">
            <!-- User Message -->
            <div v-for="message in messages" :key="message.id" class="flex justify-end">
              <div class="max-w-[75%] sm:max-w-[65%]">
                <div class="bg-blue-600 text-white rounded-2xl rounded-br-sm px-4 py-2.5 shadow-sm">
                  <p class="text-sm leading-relaxed whitespace-pre-wrap">{{ message.content }}</p>
                </div>
                <p class="text-xs text-gray-500 dark:text-gray-400 mt-1.5 px-1 text-right">{{ formatTime(message.timestamp) }}</p>
              </div>
            </div>

            <!-- AI Response -->
            <div v-for="response in responses" :key="response.id" class="flex justify-start">
              <div class="max-w-[75%] sm:max-w-[65%]">
                <div class="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-2.5 shadow-sm border border-gray-200 dark:border-gray-700">
                  <p class="text-sm text-gray-900 dark:text-white leading-relaxed whitespace-pre-wrap">{{ response.content }}</p>
                </div>
                <div class="flex items-center justify-between mt-1.5 px-1">
                  <p class="text-xs text-gray-500 dark:text-gray-400">{{ formatTime(response.timestamp) }}</p>
                  <!-- Feedback buttons -->
                  <div v-if="!response.feedbackGiven" class="flex items-center space-x-1">
                    <button 
                      @click="submitFeedback(response, 'positive')"
                      class="p-1 text-gray-400 hover:text-green-500 transition-colors"
                      title="Bra svar"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M14 10h4.764a2 2 0 011.789 2.894l-3.5 7A2 2 0 0115.263 21h-4.017c-.163 0-.326-.02-.485-.06L7 20m7-10V5a2 2 0 00-2-2h-.095c-.5 0-.905.405-.905.905 0 .714-.211 1.412-.608 2.006L7 11v9m7-10h-2M7 20H5a2 2 0 01-2-2v-6a2 2 0 012-2h2.5"></path>
                      </svg>
                    </button>
                    <button 
                      @click="submitFeedback(response, 'negative')"
                      class="p-1 text-gray-400 hover:text-red-500 transition-colors"
                      title="Dåligt svar"
                    >
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 14H5.236a2 2 0 01-1.789-2.894l3.5-7A2 2 0 018.736 3h4.018a2 2 0 01.485.06l3.76.94m-7 10v5a2 2 0 002 2h.096c.5 0 .905-.405.905-.904 0-.715.211-1.413.608-2.008L17 13V4m-7 10h2m5-10h2a2 2 0 012 2v6a2 2 0 01-2 2h-2.5"></path>
                      </svg>
                    </button>
                  </div>
                  <span v-else class="text-xs text-green-500">✓ Tack!</span>
                </div>
              </div>
            </div>

            <!-- Typing Indicator -->
            <div v-if="isTyping" class="flex justify-start">
              <div class="max-w-[75%] sm:max-w-[65%]">
                <div class="bg-gray-100 dark:bg-gray-800 rounded-2xl rounded-bl-sm px-4 py-3 shadow-sm border border-gray-200 dark:border-gray-700">
                  <div class="flex space-x-1.5">
                    <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0s"></div>
                    <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.2s"></div>
                    <div class="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style="animation-delay: 0.4s"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Input Bar (only shown when valid) - Always visible at bottom -->
      <div v-if="isValid" class="flex-shrink-0 bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-800 sticky bottom-0 z-10 safe-area-inset-bottom">
        <div class="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <div class="flex items-end space-x-3">
            <div class="flex-1 relative">
              <textarea
                v-model="newMessage"
                @keydown.enter.exact.prevent="sendMessage"
                @keydown.enter.shift.exact="newMessage += '\n'"
                @input="autoResize"
                rows="1"
                placeholder="Type your message..."
                ref="textareaRef"
                :class="[
                  'w-full px-4 py-2.5 pr-11 border border-gray-300 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 resize-none transition-all max-h-32 overflow-y-auto text-sm',
                  colorTheme === 'blue' ? 'focus:ring-blue-500 focus:border-transparent' :
                  colorTheme === 'light-blue' ? 'focus:ring-blue-400 focus:border-transparent' :
                  colorTheme === 'green' ? 'focus:ring-green-500 focus:border-transparent' :
                  colorTheme === 'orange' ? 'focus:ring-orange-500 focus:border-transparent' :
                  colorTheme === 'yellow' ? 'focus:ring-yellow-500 focus:border-transparent' :
                  colorTheme === 'red' ? 'focus:ring-red-500 focus:border-transparent' :
                  colorTheme === 'pink' ? 'focus:ring-pink-500 focus:border-transparent' :
                  'focus:ring-purple-500 focus:border-transparent'
                ]"
                style="min-height: 44px;"
              ></textarea>
              <button
                @click="sendMessage"
                :disabled="!newMessage.trim() || isTyping"
                :class="[
                  'absolute right-2 bottom-2 p-2 text-gray-400 disabled:opacity-40 disabled:cursor-not-allowed transition-colors rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700',
                  colorTheme === 'blue' ? 'hover:text-blue-600 dark:hover:text-blue-400' :
                  colorTheme === 'light-blue' ? 'hover:text-blue-500 dark:hover:text-blue-400' :
                  colorTheme === 'green' ? 'hover:text-green-600 dark:hover:text-green-400' :
                  colorTheme === 'orange' ? 'hover:text-orange-600 dark:hover:text-orange-400' :
                  colorTheme === 'yellow' ? 'hover:text-yellow-600 dark:hover:text-yellow-400' :
                  colorTheme === 'red' ? 'hover:text-red-600 dark:hover:text-red-400' :
                  colorTheme === 'pink' ? 'hover:text-pink-600 dark:hover:text-pink-400' :
                  'hover:text-purple-600 dark:hover:text-purple-400'
                ]"
                title="Send"
              >
                <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"></path>
                </svg>
              </button>
            </div>
          </div>
          <p class="hidden sm:block mt-1.5 text-xs text-gray-400 dark:text-gray-500 text-center">
            Press <kbd class="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-800 rounded text-xs font-mono">Enter</kbd> to send
          </p>
        </div>
      </div>
    </main>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, onMounted, watch } from 'vue'
import { useAuth } from './composables/useAuth'
import { apiService } from './services/api'

interface Message {
  id: string
  content: string
  timestamp: Date
  conversationId?: string
  messageId?: string
  feedbackGiven?: boolean
}

// Auth state
const { token, domain, agentId, agentName, colorTheme, isValid, isValidating, error, companyName } = useAuth()

// Get theme colors
const getThemeColors = (theme: string | null) => {
  const themes: Record<string, { gradient: string; bg: string; text: string; iconBg: string }> = {
    purple: { 
      gradient: 'linear-gradient(135deg,#667eea 0%,#764ba2 100%)',
      bg: 'bg-purple-600',
      text: 'text-purple-600',
      iconBg: 'bg-purple-50 dark:bg-purple-900/20'
    },
    blue: { 
      gradient: 'linear-gradient(135deg,#3b82f6 0%,#1e40af 100%)',
      bg: 'bg-blue-600',
      text: 'text-blue-600',
      iconBg: 'bg-blue-50 dark:bg-blue-900/20'
    },
    'light-blue': { 
      gradient: 'linear-gradient(135deg,#60a5fa 0%,#06b6d4 100%)',
      bg: 'bg-blue-400',
      text: 'text-blue-400',
      iconBg: 'bg-blue-50 dark:bg-blue-900/20'
    },
    green: { 
      gradient: 'linear-gradient(135deg,#10b981 0%,#059669 100%)',
      bg: 'bg-green-600',
      text: 'text-green-600',
      iconBg: 'bg-green-50 dark:bg-green-900/20'
    },
    orange: { 
      gradient: 'linear-gradient(135deg,#f97316 0%,#ea580c 100%)',
      bg: 'bg-orange-600',
      text: 'text-orange-600',
      iconBg: 'bg-orange-50 dark:bg-orange-900/20'
    },
    yellow: { 
      gradient: 'linear-gradient(135deg,#eab308 0%,#ca8a04 100%)',
      bg: 'bg-yellow-500',
      text: 'text-yellow-600',
      iconBg: 'bg-yellow-50 dark:bg-yellow-900/20'
    },
    red: { 
      gradient: 'linear-gradient(135deg,#ef4444 0%,#dc2626 100%)',
      bg: 'bg-red-600',
      text: 'text-red-600',
      iconBg: 'bg-red-50 dark:bg-red-900/20'
    },
    pink: { 
      gradient: 'linear-gradient(135deg,#ec4899 0%,#db2777 100%)',
      bg: 'bg-pink-600',
      text: 'text-pink-600',
      iconBg: 'bg-pink-50 dark:bg-pink-900/20'
    }
  }
  return themes[theme || 'purple'] || themes.purple
}

const themeColors = computed(() => getThemeColors(colorTheme.value))

const newMessage = ref('')
const messages = ref<Message[]>([])
const responses = ref<Message[]>([])
const isTyping = ref(false)
const messagesContainer = ref<HTMLElement>()
const textareaRef = ref<HTMLTextAreaElement>()
const currentConversationId = ref<string | null>(null)
// Session ID for tracking conversations (used by backend)
const _sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
void _sessionId // Mark as intentionally unused for now

const autoResize = () => {
  if (textareaRef.value) {
    textareaRef.value.style.height = 'auto'
    textareaRef.value.style.height = `${Math.min(textareaRef.value.scrollHeight, 128)}px`
  }
}

// Build conversation history for API
const buildConversationHistory = (): Array<{ role: 'user' | 'assistant', content: string }> => {
  const history: Array<{ role: 'user' | 'assistant', content: string }> = []
  
  // Interleave messages and responses based on their order
  const allMessages = [
    ...messages.value.map(m => ({ ...m, type: 'user' as const })),
    ...responses.value.map(r => ({ ...r, type: 'assistant' as const }))
  ].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime())
  
  for (const msg of allMessages) {
    history.push({
      role: msg.type,
      content: msg.content
    })
  }
  
  return history
}

const sendMessage = async () => {
  if (!newMessage.value.trim() || isTyping.value || !isValid.value || !token.value) return

  const message: Message = {
    id: Date.now().toString(),
    content: newMessage.value.trim(),
    timestamp: new Date()
  }

  messages.value.push(message)
  const currentMessage = newMessage.value
  newMessage.value = ''
  
  if (textareaRef.value) {
    textareaRef.value.style.height = '44px'
  }

  await nextTick()
  scrollToBottom()

  isTyping.value = true

  try {
    // Build conversation history (excluding the message we just added - it will be sent as 'message')
    const history = buildConversationHistory().slice(0, -1) // Remove last message (current one)
    
    // Call API with token, domain, agentId and conversation history
    const result = await apiService.sendMessage(
      token.value,
      currentMessage,
      domain.value || '',
      agentId.value || undefined,
      agentName.value || undefined,
      history
    )

    if (result.success && result.data) {
      // Store conversation ID for future messages
      if (result.data.conversationId) {
        currentConversationId.value = result.data.conversationId
      }
      
      const response: Message = {
        id: (Date.now() + 1).toString(),
        content: result.data.response || 'I received your message.',
        timestamp: new Date(),
        conversationId: result.data.conversationId,
        messageId: result.data.messageId,
        feedbackGiven: false
      }
      responses.value.push(response)
    } else {
      // Fallback to mock response if API fails
      const response: Message = {
        id: (Date.now() + 1).toString(),
        content: result.error || await generateAIResponse(currentMessage),
        timestamp: new Date(),
        feedbackGiven: false
      }
      responses.value.push(response)
    }
  } catch (err) {
    // Fallback to mock response on error
    const response: Message = {
      id: (Date.now() + 1).toString(),
      content: await generateAIResponse(currentMessage),
      timestamp: new Date(),
      feedbackGiven: false
    }
    responses.value.push(response)
  } finally {
    isTyping.value = false
    await nextTick()
    scrollToBottom()
  }
}

// Submit feedback for a response
const submitFeedback = async (response: Message, feedbackType: 'positive' | 'negative') => {
  console.log('Submitting feedback:', { 
    messageId: response.messageId, 
    conversationId: response.conversationId, 
    feedbackType 
  })
  
  if (!response.messageId || !response.conversationId) {
    console.warn('Missing IDs for feedback submission:', { messageId: response.messageId, conversationId: response.conversationId })
    // Mark as given even if we can't submit (no IDs)
    response.feedbackGiven = true
    return
  }
  
  try {
    const result = await apiService.submitFeedback(
      response.messageId,
      response.conversationId,
      feedbackType
    )
    console.log('Feedback submission result:', result)
    response.feedbackGiven = true
  } catch (err) {
    console.error('Failed to submit feedback:', err)
    // Still mark as given to avoid multiple submissions
    response.feedbackGiven = true
  }
}

const generateAIResponse = async (_userMessage: string): Promise<string> => {
  const responses = [
    "I understand your question. Let me help you with that.",
    "That's a great question! Here's what I can tell you...",
    "I'm here to assist you. Based on what you've shared...",
    "Thank you for reaching out. I'd be happy to help with that.",
    "I can definitely help you with this. Let me provide some information...",
    "That's an interesting point. Let me give you a detailed response...",
  ]

  await new Promise(resolve => setTimeout(resolve, 500))
  return responses[Math.floor(Math.random() * responses.length)]
}

const formatTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const scrollToBottom = () => {
  if (messagesContainer.value) {
    messagesContainer.value.scrollTo({ top: messagesContainer.value.scrollHeight, behavior: 'smooth' })
  }
}

watch([messages, responses], () => {
  nextTick(() => scrollToBottom())
})

onMounted(() => {
  if (textareaRef.value) {
    textareaRef.value.focus()
  }
})
</script>

<style scoped>
@keyframes bounce {
  0%, 80%, 100% {
    transform: translateY(0);
    opacity: 0.5;
  }
  40% {
    transform: translateY(-6px);
    opacity: 1;
  }
}

.animate-bounce {
  animation: bounce 1.4s infinite ease-in-out both;
}
</style>